import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import { site } from "@/data/enrichments";

const CLAUSES = [
  { num: "I",   text: "Fill us with light." },
  { num: "II",  text: "Renew us." },
  { num: "III", text: "Fill us with awe through learning." },
  { num: "IV",  text: "Bring the scriptures closer to our hearts." },
];

const TRADITIONS = [
  "Hindu",
  "Buddhist",
  "Christian",
  "Sufi",
  "Sikh",
  "Jain",
  "Zen",
];

export default function About() {
  return (
    <PageShell title="About">
      <PageHeader
        eyebrow="About"
        title="A small studio at the foot of an old library."
        lede="DharmaWeave is a creative practice making graphic novels, songs, and films from the world's living scriptures. We are a Tara Ventures brand."
      />

      <section className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">Mission</p>
            <div className="space-y-5 max-w-prose">
              {CLAUSES.map((c, i) => (
                <motion.div
                  key={c.num}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.8,
                    delay: i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="flex items-start gap-5"
                >
                  <div className="font-display text-dharma-gold-deep w-7 text-base tracking-widest2 pt-1 shrink-0">
                    {c.num}.
                  </div>
                  <p className="font-serif text-ink text-2xl md:text-3xl leading-snug">
                    {c.text}
                  </p>
                </motion.div>
              ))}
            </div>
            <p className="font-serif italic text-ink-mute text-lg mt-10 max-w-prose">
              These are the principles which guide everything we make.
            </p>
          </div>

          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">Traditions we draw from</p>
            <div className="flex flex-wrap gap-2">
              {TRADITIONS.map((t) => (
                <span key={t} className="tradition-tag">
                  {t}
                </span>
              ))}
            </div>

            <div className="rule-gold my-10" />

            <p className="eyebrow mb-5">Newsletter</p>
            <p className="font-serif text-ink-soft text-lg leading-relaxed">
              Once a week, a single piece of writing — usually a verse and a
              note on what it points to.
            </p>
            <a
              href="https://substack.com/@dharmaweave"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-5 btn-pill btn-pill-solid"
            >
              Read on Substack
              <ArrowUpRight size={14} />
            </a>

            <div className="rule-gold my-10" />

            <p className="eyebrow mb-5">Contact</p>
            <a
              href={`mailto:${site.contact_email}`}
              className="font-serif text-xl text-ink hover:text-dharma-gold-deep transition-colors"
            >
              {site.contact_email}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-shrine-shadow text-cream">
        <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="eyebrow text-dawn-rose mb-5">The long game</p>
            <p className="font-serif text-cream text-2xl md:text-3xl leading-snug text-balance">
              To increase faith, love, and spiritual evolution in society — to
              be the place where someone scrolling past the algorithmic noise
              lands and feels something true land in their chest.
            </p>
            <p className="font-serif italic text-cream/60 text-base md:text-lg mt-7">
              The website is the home where every instrument lives together —
              so a visitor who arrived through one (a reel, a song) can
              discover the others, and feel the coherent practice behind them.
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
